// ABC SEO/GEO — Connections screen. Reads LIVE data from the deployed Worker API.
(function () {
  const { Icon } = window.SGIcons;
  const { Card, CardHead, PageHead } = window.SGKit;

  // All API calls go through SGLive.apiFetch, which resolves the key from the
  // GHL embedded-app session (or a dev-only env key) — no admin key in source.
  const apiFetch = window.SGLive.apiFetch;

  const BRAND = {
    google_search_console: { color: '#4285F4', tag: 'GSC' },
    google_analytics:      { color: '#E8710A', tag: 'GA' },
    google_business:       { color: '#34A853', tag: 'GBP' },
    youtube:               { color: '#FF0000', tag: 'YT' },
    bing_webmaster:        { color: '#0A6ED1', tag: 'B' },
  };

  function StatusPill({ status }) {
    const map = {
      connected:     { c: 'var(--sg-green)',  bg: 'var(--sg-green-50)', label: 'Connected' },
      not_connected: { c: 'var(--sg-text-2)', bg: 'var(--sg-sunken)',   label: 'Not connected' },
      error:         { c: 'var(--sg-red)',    bg: 'var(--sg-red-50)',   label: 'Action needed' },
    };
    const m = map[status] || map.not_connected;
    return (
      <span style={{ fontSize: 11.5, fontWeight: 700, color: m.c, background: m.bg,
        padding: '3px 10px', borderRadius: 'var(--sg-radius-pill)', whiteSpace: 'nowrap' }}>
        {status === 'connected' ? '● ' : ''}{m.label}
      </span>
    );
  }

  function ProviderCard({ p, onConnect }) {
    const ic = BRAND[p.provider] || { color: '#888', tag: '?' };
    const connected = p.status === 'connected';
    return (
      <Card style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 14,
        animation: 'sg-rise 0.4s var(--sg-ease) both' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ width: 40, height: 40, borderRadius: 11, background: ic.color, color: '#fff',
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700,
            fontSize: 13, fontFamily: 'var(--sg-font-display)' }}>{ic.tag}</span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontFamily: 'var(--sg-font-display)', fontWeight: 600, fontSize: 15, color: 'var(--sg-text)' }}>{p.label}</div>
            <div style={{ fontSize: 12.5, color: 'var(--sg-text-2)' }}>{p.benefit}</div>
          </div>
          <StatusPill status={p.status} />
        </div>
        {connected && (
          <div style={{ fontSize: 12.5, color: 'var(--sg-text)', background: 'var(--sg-green-50)',
            padding: '8px 12px', borderRadius: 'var(--sg-radius-md)', display: 'flex', alignItems: 'center', gap: 8 }}>
            <Icon name="check" size={14} color="var(--sg-green)" />
            {p.account_email || 'connected'}{p.property_label ? ' · ' + p.property_label : ''}
          </div>
        )}
        <button onClick={() => onConnect(p)} style={{ height: 40, border: connected ? '1px solid var(--sg-border)' : 'none',
          borderRadius: 'var(--sg-radius-pill)', cursor: 'pointer', fontFamily: 'var(--sg-font-sans)', fontWeight: 700, fontSize: 13.5,
          color: connected ? 'var(--sg-text-2)' : '#fff', background: connected ? 'var(--sg-card)' : 'var(--sg-grad-brand)' }}>
          {connected ? 'Disconnect' : 'Connect'}
        </button>
      </Card>
    );
  }

  // Tracked-domains meter + manager (per-membership billing unit).
  function DomainsCard() {
    const prettySite = window.SGLive.prettySite;
    const [acc, reloadAcc] = window.SGLive.useAccount();
    const [available, setAvailable] = React.useState(null);
    const [pick, setPick] = React.useState('');
    const [manual, setManual] = React.useState('');
    const [msg, setMsg] = React.useState(null);
    const [busy, setBusy] = React.useState(false);

    React.useEffect(() => { window.SGLive.fetchSites().then(setAvailable).catch(() => setAvailable([])); }, []);

    if (!acc) return <Card style={{ padding: 28, color: 'var(--sg-text-2)' }}>Loading your plan…</Card>;

    const tracked = (acc.domains || []).map((d) => d.property);
    const untracked = (available || []).filter((s) => tracked.indexOf(s) < 0);
    const atLimit = acc.used >= acc.domainLimit;
    const pct = Math.min(100, Math.round((acc.used / Math.max(1, acc.domainLimit)) * 100));
    const planLabel = (acc.plan || 'starter').charAt(0).toUpperCase() + (acc.plan || 'starter').slice(1);

    // Normalize a manually-typed domain → a Search Console "domain property"
    // key (sc-domain:example.com). If the domain is verified in GSC, live data
    // flows; if not, the domain still works for audits / GEO / keyword research.
    const normalizeManual = (raw) => {
      let s = (raw || '').trim().toLowerCase();
      if (!s) return '';
      if (s.indexOf('sc-domain:') === 0 || s.indexOf('http') === 0) return s; // already a property key
      s = s.replace(/^www\./, '').replace(/\/.*$/, '');
      return 'sc-domain:' + s;
    };

    const doAdd = async (property) => {
      if (!property) return;
      setBusy(true); setMsg(null);
      const r = await window.SGLive.addDomain(property, prettySite(property));
      setBusy(false);
      if (r && r.success === false) setMsg(r.error || 'Could not add domain');
      else { setPick(''); setManual(''); reloadAcc(); }
    };
    const add = () => doAdd(pick || untracked[0]);
    const addManual = () => {
      const p = normalizeManual(manual);
      if (!p) { setMsg('Type a domain like example.com'); return; }
      if (tracked.indexOf(p) >= 0) { setMsg('That domain is already tracked.'); return; }
      doAdd(p);
    };
    const remove = async (p) => { setBusy(true); await window.SGLive.removeDomain(p); setBusy(false); reloadAcc(); };

    return (
      <Card>
        <CardHead title="Tracked domains" sub="Each membership tracks a set number of domains. Add the ones you want analyzed."
          right={<span style={{ fontSize: 11.5, fontWeight: 700, color: 'var(--sg-violet-700)', background: 'var(--sg-violet-100)', padding: '3px 10px', borderRadius: 'var(--sg-radius-pill)' }}>{planLabel} plan</span>} />
        <div style={{ padding: 20 }}>
          {/* meter */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
            <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--sg-text-2)' }}>Domains used</span>
            <span className="sg-tnum" style={{ fontSize: 13, fontWeight: 700, color: atLimit ? 'var(--sg-amber)' : 'var(--sg-text)' }}>{acc.used} of {acc.domainLimit}</span>
          </div>
          <div style={{ height: 8, borderRadius: 999, background: 'var(--sg-sunken)', overflow: 'hidden', marginBottom: 18 }}>
            <div style={{ width: pct + '%', height: '100%', borderRadius: 999, background: atLimit ? 'var(--sg-amber)' : 'var(--sg-grad-brand)' }} />
          </div>

          {/* tracked list */}
          {tracked.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 16 }}>
              {acc.domains.map((d) => (
                <div key={d.property} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px', border: '1px solid var(--sg-border)', borderRadius: 'var(--sg-radius-md)' }}>
                  <Icon name="globe" size={16} color="var(--sg-blue-600)" />
                  <span style={{ flex: 1, fontSize: 13.5, fontWeight: 600, color: 'var(--sg-text)' }}>{prettySite(d.property)}</span>
                  <button onClick={() => remove(d.property)} disabled={busy} aria-label="Remove"
                    style={{ width: 28, height: 28, borderRadius: 7, border: '1px solid var(--sg-border)', background: 'var(--sg-card)', cursor: 'pointer', color: 'var(--sg-text-3)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Icon name="x" size={14} />
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* add row OR upgrade prompt */}
          {atLimit ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 14px', borderRadius: 'var(--sg-radius-md)', background: 'var(--sg-amber-50)', border: '1px solid var(--sg-amber-100, #F6E6C7)' }}>
              <Icon name="zap" size={16} color="var(--sg-amber)" />
              <span style={{ flex: 1, fontSize: 13, color: 'var(--sg-text)' }}>You’ve used all {acc.domainLimit} domains on the {planLabel} plan.</span>
              <button onClick={() => { try { window.parent && window.parent.postMessage({ type: 'abc-upgrade' }, '*'); } catch (e) { /* noop */ } }} style={{ height: 36, padding: '0 16px', border: 'none', borderRadius: 'var(--sg-radius-pill)', cursor: 'pointer', fontFamily: 'var(--sg-font-sans)', fontWeight: 700, fontSize: 13, color: '#fff', background: 'var(--sg-grad-brand)' }}>Upgrade plan</button>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {/* Auto-discovered (verified in Google Search Console) */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <select value={pick} onChange={(e) => setPick(e.target.value)} disabled={!untracked.length}
                  style={{ flex: 1, height: 42, padding: '0 12px', border: '1px solid var(--sg-border)', borderRadius: 'var(--sg-radius-md)', background: 'var(--sg-card)', fontFamily: 'var(--sg-font-sans)', fontSize: 13.5, color: 'var(--sg-text)', cursor: untracked.length ? 'pointer' : 'not-allowed' }}>
                  <option value="">{untracked.length ? 'Choose a verified domain to add…' : (available ? 'No auto-detected domains — add one manually below' : 'Loading domains…')}</option>
                  {untracked.map((s) => <option key={s} value={s}>{prettySite(s)}</option>)}
                </select>
                <button onClick={add} disabled={busy || !untracked.length}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 7, height: 42, padding: '0 18px', border: 'none', borderRadius: 'var(--sg-radius-pill)', cursor: untracked.length ? 'pointer' : 'not-allowed', fontFamily: 'var(--sg-font-sans)', fontWeight: 700, fontSize: 13.5, color: '#fff', background: 'var(--sg-grad-brand)', opacity: untracked.length ? 1 : 0.5 }}>
                  <Icon name="plus" size={16} color="#fff" /> Add
                </button>
              </div>

              {/* Manual entry — any domain (also covers Analytics / Bing properties) */}
              <div>
                <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--sg-text-3)', marginBottom: 6 }}>Or add any domain manually</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <input value={manual} onChange={(e) => setManual(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') addManual(); }}
                    placeholder="example.com"
                    style={{ flex: 1, height: 42, padding: '0 14px', border: '1px solid var(--sg-border)', borderRadius: 'var(--sg-radius-md)', background: 'var(--sg-card)', fontFamily: 'var(--sg-font-sans)', fontSize: 13.5, color: 'var(--sg-text)', outline: 'none' }} />
                  <button onClick={addManual} disabled={busy || !manual.trim()}
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 7, height: 42, padding: '0 18px', border: '1px solid var(--sg-blue-600)', borderRadius: 'var(--sg-radius-pill)', cursor: manual.trim() ? 'pointer' : 'not-allowed', fontFamily: 'var(--sg-font-sans)', fontWeight: 700, fontSize: 13.5, color: 'var(--sg-blue-600)', background: 'var(--sg-card)', opacity: manual.trim() ? 1 : 0.5 }}>
                    <Icon name="plus" size={16} color="var(--sg-blue-600)" /> Add
                  </button>
                </div>
                <div style={{ fontSize: 11.5, color: 'var(--sg-text-3)', marginTop: 6, lineHeight: 1.5 }}>
                  Search Console &amp; Analytics data shows when the domain is verified on the connected Google account; otherwise you still get the site audit, AI-visibility and keyword tools.
                </div>
              </div>
            </div>
          )}
          {msg && <div style={{ marginTop: 10, fontSize: 12.5, color: 'var(--sg-amber)' }}>{msg}</div>}
        </div>
      </Card>
    );
  }

  /* Your market + industry. Rank checks, keyword volumes and AI-visibility prompts
     are all measured against these, so a Richmond Hill realtor is scored on the
     Richmond Hill SERP — not a national average. */
  function MarketCard() {
    const { site, prettySite } = window.SGLive.useLive();
    const [p, setP] = React.useState(null);
    const [loc, setLoc] = React.useState('');
    const [biz, setBiz] = React.useState('');
    const [brand, setBrand] = React.useState('');
    const [busy, setBusy] = React.useState(false);
    const [msg, setMsg] = React.useState(null);
    const [det, setDet] = React.useState(null);     // what we read off the website
    const [reading, setReading] = React.useState(false);
    const clean = (v) => (/^\s*united states\s*$/i.test(v || '') ? '' : (v || ''));
    // Re-read when the domain changes — each site has its own market.
    React.useEffect(() => {
      setP(null); setMsg(null); setDet(null);
      window.SGLive.fetchProfile().then((d) => {
        if (!d) return;
        setP(d); setLoc(clean(d.target_location)); setBiz(d.business_type || ''); setBrand(d.brand || '');
      });
      // The stored reading of this site — no crawl, just what we already learned.
      window.SGLive.fetchDetectedProfile().then(setDet);
    }, [site]);
    // Crawl the site again and fill in whatever is still blank.
    const detect = () => {
      if (reading) return;
      setReading(true); setMsg(null);
      window.SGLive.detectProfile(false).then((r) => {
        setReading(false);
        if (!r.ok) { setMsg({ ok: false, text: r.error }); return; }
        setDet(r.profile);
        // Show the newly-filled values without clobbering anything already typed.
        if (r.profile) {
          if (!brand.trim() && r.profile.brand) setBrand(r.profile.brand);
          if (!loc.trim() && r.profile.location) setLoc(r.profile.location);
          if (!biz.trim() && r.profile.businessType) setBiz(r.profile.businessType);
        }
        const n = Object.keys(r.applied || {}).length;
        setMsg({ ok: true, text: n ? 'Read your website and filled in ' + n + ' field' + (n > 1 ? 's' : '') + '.' : 'Read your website — nothing new to fill in.' });
      });
    };
    const dirty = p && (loc.trim() !== clean(p.target_location) || biz.trim() !== (p.business_type || '') || brand.trim() !== (p.brand || ''));
    const save = () => {
      if (busy) return;
      setBusy(true); setMsg(null);
      window.SGLive.saveProfile({ target_location: loc.trim(), business_type: biz.trim(), brand: brand.trim() }).then((r) => {
        setBusy(false);
        if (r.ok) { setP(r.data); setMsg({ ok: true, text: 'Saved — run “Check rankings now” to re-measure against your market.' }); }
        else setMsg({ ok: false, text: r.error });
      });
    };
    const inp = { width: '100%', height: 42, padding: '0 14px', border: '1px solid var(--sg-border)', borderRadius: 'var(--sg-radius-md)', background: 'var(--sg-card)', fontFamily: 'var(--sg-font-sans)', fontSize: 13.5, color: 'var(--sg-text)', outline: 'none', boxSizing: 'border-box' };
    return (
      <Card>
        <CardHead title={`Market for ${site ? prettySite(site) : 'this site'}`} icon="globe"
          sub="Set per website — rankings and AI-visibility prompts for this site are measured for this city and industry. Each of your sites can target a different market."
          right={p && p.inherited ? <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', color: 'var(--sg-text-3)', background: 'var(--sg-sunken)', padding: '3px 9px', borderRadius: 'var(--sg-radius-pill)' }}>Using account default</span> : null} />
        <div style={{ padding: 18 }}>
          {!p ? <div style={{ fontSize: 13, color: 'var(--sg-text-2)' }}>Loading…</div> : (
            <React.Fragment>
              <div style={{ marginBottom: 14 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--sg-text-2)', marginBottom: 6 }}>Brand name for this site</div>
                <input value={brand} onChange={(e) => setBrand(e.target.value)} placeholder="e.g. Ali Bolourchi" style={inp} />
                <div style={{ fontSize: 11.5, color: 'var(--sg-text-3)', marginTop: 5 }}>The name people actually search for. AI-visibility prompts ask about this — one account can run several unrelated brands.</div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                <div>
                  <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--sg-text-2)', marginBottom: 6 }}>City / area you serve</div>
                  <input value={loc} onChange={(e) => setLoc(e.target.value)} placeholder="e.g. Richmond Hill, Ontario" style={inp} />
                  <div style={{ fontSize: 11.5, color: 'var(--sg-text-3)', marginTop: 5 }}>Name a city to get the rankings your local customers actually see. Leave blank for country-wide.</div>
                </div>
                <div>
                  <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--sg-text-2)', marginBottom: 6 }}>Industry</div>
                  <input value={biz} onChange={(e) => setBiz(e.target.value)} placeholder="e.g. real estate agent" style={inp} />
                  <div style={{ fontSize: 11.5, color: 'var(--sg-text-3)', marginTop: 5 }}>Used to generate the AI-visibility prompts and keyword ideas for your trade.</div>
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 14 }}>
                <button onClick={save} disabled={!dirty || busy}
                  style={{ height: 40, padding: '0 20px', border: 'none', borderRadius: 'var(--sg-radius-pill)', cursor: (!dirty || busy) ? 'not-allowed' : 'pointer', opacity: (!dirty || busy) ? 0.5 : 1, fontFamily: 'var(--sg-font-sans)', fontWeight: 700, fontSize: 13.5, color: '#fff', background: 'var(--sg-grad-brand)' }}>
                  {busy ? 'Saving…' : 'Save market'}
                </button>
                <button onClick={detect} disabled={reading}
                  style={{ height: 40, padding: '0 18px', borderRadius: 'var(--sg-radius-pill)', border: '1px solid var(--sg-border)', background: 'var(--sg-card)', color: 'var(--sg-text)', cursor: reading ? 'wait' : 'pointer', fontFamily: 'var(--sg-font-sans)', fontWeight: 700, fontSize: 13 }}>
                  {reading ? 'Reading your website…' : 'Read from my website'}
                </button>
                {msg && <span style={{ fontSize: 12.5, color: msg.ok ? 'var(--sg-green)' : 'var(--sg-red, #DC2626)' }}>{msg.text}</span>}
              </div>
              {det && (det.brand || det.businessType || det.location) ? (
                <div style={{ marginTop: 14, padding: 14, borderRadius: 'var(--sg-radius-md)', background: 'var(--sg-sunken)', border: '1px solid var(--sg-border)' }}>
                  <div style={{ fontSize: 11.5, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: 'var(--sg-text-3)', marginBottom: 8 }}>
                    Read from your website
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                    {[['Brand', det.brand, det.sources && det.sources.brand],
                      ['Industry', det.businessType, det.sources && det.sources.businessType],
                      ['Location', det.location, det.sources && det.sources.location]]
                      .filter((r) => r[1]).map((r) => (
                      <span key={r[0]} style={{ fontSize: 12, color: 'var(--sg-text)', background: 'var(--sg-card)', border: '1px solid var(--sg-border)', borderRadius: 'var(--sg-radius-pill)', padding: '4px 11px' }}>
                        <strong>{r[0]}:</strong> {r[1]}
                        {r[2] ? <span style={{ color: 'var(--sg-text-3)' }}> · {r[2] === 'schema' ? 'structured data' : r[2] === 'meta' ? 'page meta' : r[2] === 'content' ? 'page text' : r[2] === 'ai' ? 'inferred' : r[2]}</span> : null}
                      </span>
                    ))}
                  </div>
                  {det.services && det.services.length ? (
                    <div style={{ fontSize: 12, color: 'var(--sg-text-2)', marginTop: 9 }}>
                      <strong>Services found:</strong> {det.services.slice(0, 6).join(' · ')}
                    </div>
                  ) : null}
                  {det.pagesRead && det.pagesRead.length ? (
                    <div style={{ fontSize: 11.5, color: 'var(--sg-text-3)', marginTop: 7 }}>
                      Read {det.pagesRead.length} page{det.pagesRead.length > 1 ? 's' : ''}{det.confidence ? ' · confidence: ' + det.confidence : ''}
                    </div>
                  ) : null}
                </div>
              ) : null}
            </React.Fragment>
          )}
        </div>
      </Card>
    );
  }

  /* Auto-pilot: the recurring jobs that already run for this account, exposed as
     toggles so the client can see (and control) what the platform does for them. */
  const AUTOS = [
    { key: 'auto_rank', icon: 'trending-up', name: 'Daily rank tracking', sub: 'Checks your approved keywords’ Google positions every day.' },
    { key: 'auto_geo', icon: 'sparkles', name: 'Daily AI-visibility scan', sub: 'Asks the AI engines about your brand and tracks whether you’re cited.' },
    { key: 'auto_audit', icon: 'activity', name: 'Weekly site audit', sub: 'Re-crawls your site every Monday and refreshes issues, speed & AI readiness.' },
  ];
  function AutomationsCard() {
    const [state, setState] = React.useState(null);
    const [saving, setSaving] = React.useState(null);
    React.useEffect(() => { window.SGLive.fetchAutomations().then(setState); }, []);
    const toggle = (key) => {
      if (!state || saving) return;
      const next = !state[key];
      setSaving(key);
      setState((s) => ({ ...s, [key]: next }));
      window.SGLive.setAutomation({ [key]: next }).then((ok) => {
        setSaving(null);
        if (!ok) setState((s) => ({ ...s, [key]: !next })); // revert on failure
      });
    };
    return (
      <Card>
        <CardHead title="Automations" icon="clock"
          sub="Running on auto-pilot for you — no clicking required. Turn any of it off if you'd rather run it yourself." />
        {!state
          ? <div style={{ padding: 20, fontSize: 13, color: 'var(--sg-text-2)' }}>Loading your automations…</div>
          : AUTOS.map((a, i) => (
            <div key={a.key} style={{ display: 'flex', alignItems: 'center', gap: 13, padding: '14px 20px', borderTop: i ? '1px solid var(--sg-border)' : 'none' }}>
              <span style={{ width: 36, height: 36, flex: 'none', borderRadius: 10, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                background: state[a.key] ? 'var(--sg-blue-50)' : 'var(--sg-sunken)', color: state[a.key] ? 'var(--sg-blue-600)' : 'var(--sg-text-3)' }}><Icon name={a.icon} size={17} /></span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--sg-text)' }}>{a.name}</div>
                <div style={{ fontSize: 12, color: 'var(--sg-text-3)', marginTop: 2 }}>{a.sub}</div>
              </div>
              <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', color: state[a.key] ? 'var(--sg-green)' : 'var(--sg-text-3)' }}>{state[a.key] ? 'On' : 'Off'}</span>
              {/* switch */}
              <button onClick={() => toggle(a.key)} disabled={saving === a.key} aria-label={'Toggle ' + a.name}
                style={{ width: 46, height: 26, flex: 'none', borderRadius: 999, border: 'none', cursor: saving === a.key ? 'wait' : 'pointer', padding: 3,
                  background: state[a.key] ? 'var(--sg-grad-brand)' : 'var(--sg-border)', display: 'flex', justifyContent: state[a.key] ? 'flex-end' : 'flex-start', transition: 'all 0.18s var(--sg-ease)' }}>
                <span style={{ width: 20, height: 20, borderRadius: '50%', background: '#fff', boxShadow: '0 1px 3px rgba(0,0,0,0.25)' }} />
              </button>
            </div>
          ))}
      </Card>
    );
  }

  function Connections() {
    const [data, setData] = React.useState(null);
    const [err, setErr] = React.useState(null);

    const load = React.useCallback(() => {
      setErr(null);
      apiFetch('/api/connections')
        .then((r) => r.json())
        .then((j) => setData(j.data || []))
        .catch((e) => setErr(String(e)));
    }, []);
    React.useEffect(() => { load(); }, [load]);

    // The OAuth popup posts {type:'abc-connected'} when it finishes; refresh status.
    // Also refresh when the user returns to this tab (in case they closed the popup).
    React.useEffect(() => {
      const onMsg = (e) => { if (e.data && e.data.type === 'abc-connected') load(); };
      const onVis = () => { if (document.visibilityState === 'visible') load(); };
      window.addEventListener('message', onMsg);
      document.addEventListener('visibilitychange', onVis);
      return () => { window.removeEventListener('message', onMsg); document.removeEventListener('visibilitychange', onVis); };
    }, [load]);

    const onConnect = (p) => {
      if (p.status === 'connected') {
        apiFetch('/api/connections/' + p.provider, { method: 'DELETE' }).then(() => load());
        return;
      }
      const path = p.provider === 'bing_webmaster'
        ? '/api/connections/bing/start'
        : '/api/connections/google/start?providers=' + ({ google_search_console: 'gsc', google_analytics: 'ga', google_business: 'gbp', youtube: 'youtube' }[p.provider] || 'gsc');
      apiFetch(path).then((r) => r.json()).then((j) => {
        const url = j.data && j.data.authUrl;
        // Same-tab (top-window) redirect, not a popup — popups get silently blocked
        // by browsers and inside the GoHighLevel iframe. The connected page links back.
        if (url) { try { (window.top || window).location.assign(url); } catch (e) { window.location.assign(url); } }
      });
    };

    return (
      <div style={{ maxWidth: 1080, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: 20 }}>
        <PageHead
          eyebrow="SETTINGS" title="Connections" icon="link"
          sub="Connect your accounts to see your real search & business data — not just estimates. This screen reads LIVE from the deployed AI Biz Connect platform API."
          right={<button onClick={load} style={{ height: 42, padding: '0 16px', border: '1px solid var(--sg-border)',
            borderRadius: 'var(--sg-radius-pill)', background: 'var(--sg-card)', cursor: 'pointer',
            fontFamily: 'var(--sg-font-sans)', fontWeight: 600, fontSize: 13.5, color: 'var(--sg-text)' }}>↻ Refresh</button>}
        />
        {err && <Card style={{ padding: 16, color: 'var(--sg-red)' }}>Couldn't reach the live API: {err}</Card>}
        {!data && !err && <Card style={{ padding: 40, textAlign: 'center', color: 'var(--sg-text-2)' }}>Loading live connection status…</Card>}
        {data && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 16 }}>
            {data.map((p) => <ProviderCard key={p.provider} p={p} onConnect={onConnect} />)}
          </div>
        )}

        <MarketCard />
        <DomainsCard />
        <AutomationsCard />
        <div style={{ fontSize: 12, color: 'var(--sg-text-3)', textAlign: 'center' }}>
          Read-only where possible · disconnect anytime · we never sell your data.
        </div>
      </div>
    );
  }

  window.SGConnections = Connections;
})();
